/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import DS from 'ember-data';
import { underscore } from '@ember/string';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import EmObject, { action, computed } from '@ember/object';
import { not } from '@ember/object/computed';
import AuthService from 'cio/services/auth';
import { task } from 'ember-concurrency';
import AnalyticsService from 'cio/services/analytics';
import AccountService from 'cio/services/account';
import CustomFormsService, { ScannedForm } from 'cio/services/custom-forms';
import CustomForm, { CustomFormStatus, CustomFormAttrMap } from 'cio/models/custom-form';
import EnvironmentService from 'cio/services/environment';
import FormValidations from 'cio/utils/form-validations';
import joinWords from 'cio/utils/join-words';
import { isEmpty } from '@ember/utils';
import { isPotentiallyValidUrl } from 'cio/utils/validators/url';
import { next } from '@ember/runloop';
import { buildWaiter } from '@ember/test-waiters';

export enum Tabs {
  FindForm = 'find-form',
  SelectForm = 'select-form',
  MapFormFields = 'map-form-fields',
  Success = 'success',
}

export interface FormsCustomFormSetupFlowArgs {
  onClose: () => unknown;
  onConnectForm?: (form: CustomForm) => unknown;
  onConnectFormAndClose?: (form: CustomForm) => unknown;
  onSelectForm?: (form: CustomForm) => unknown;
  onTabChange?: (value: Tabs) => unknown;
  onConnectedFormChange?: (value: boolean) => unknown;
  onShowFormProvidersModal?: () => unknown;
  form: CustomForm;
  isRescanning?: boolean;
  source: string;
  campaignSidebar?: boolean;
  isInModal?: boolean;
  isDuplicatingForm?: boolean;
}

const FORM_WAITER = buildWaiter('custom-form-submit-task');

const validations = {
  urlToScanForForms(this: FormsCustomSetupFlow, url: string) {
    if (!isPotentiallyValidUrl(url) || !url.includes('.')) {
      return 'Form URL must be a valid link';
    }

    // eslint-disable-next-line spellcheck/spell-checker
    if ((url.includes('google.com') && url.includes('viewform')) || url.includes('forms.gle')) {
      return `Looks like you're using Google Forms! To connect this, use our <a
      target="_blank" rel="noopener noreferrer" href="https://zapier.com/apps/google-forms/integrations/customerio">Zapier integration</a>.`;
    } else if (url.includes('.typeform.com/')) {
      return `Looks like you're using Typeform! To connect this form, use our <a
      target="_blank" rel="noopener noreferrer" href="https://customer.io/docs/typeform">native Typeform integration</a>.`;
    }

    return true;
  },
};

type AlertType = {
  type: 'warning' | 'error';
};

type ErrorMessage = {
  message: string;
  eventErrorText: string;
};

export default class FormsCustomSetupFlow extends Component<FormsCustomFormSetupFlowArgs> {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service customForms!: CustomFormsService;
  @service account!: AccountService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;

  tabs = Tabs;
  cheerImage = new URL('cio/assets/images/cheer.png', import.meta.url).toString();
  private validations!: FormValidations;
  @tracked formValues: { urlToScanForForms: string };
  @tracked currentTab: Tabs | null = null;
  @tracked scannedForms: ScannedForm[] | null = null;
  @tracked selectedScannedForm: ScannedForm | null = null;
  @tracked alert: AlertType | null = null;
  @tracked connectedForm = false;

  constructor(owner: unknown, args: FormsCustomFormSetupFlowArgs) {
    super(owner, args);
    this.validations = FormValidations.create({
      validations,
      context: this,
      modelKey: 'formValues',
    });

    this.formValues = EmObject.create({ urlToScanForForms: args.isRescanning === true ? args.form.htmlFormUrl : '' });

    next(() => {
      this.goToTab(args.form.get('isNew') || args.isRescanning === true ? Tabs.FindForm : Tabs.MapFormFields);
      this.args.onConnectedFormChange?.(this.connectedForm);
    });
  }

  get identifierAttributes() {
    return Object.keys(this.environment.current.identifiers).filter((key) => key !== 'cio_id');
  }

  @computed('args.form.id', 'customForms.forms.@each.htmlFingerprint', 'selectedScannedForm.generatedHTMLFingerprint')
  get duplicateForm() {
    return this.customForms.forms.find(
      (form) =>
        form.id !== this.args.form.id && form.htmlFingerprint === this.selectedScannedForm?.generatedHTMLFingerprint,
    );
  }

  get errorMessages(): Record<string, ErrorMessage> {
    let duplicateFormBase = `A ${
      this.duplicateForm?.isDisconnected === true ? 'disconnected ' : ''
    }form with the same fields already exists.`;

    return {
      duplicateForm: {
        message: duplicateFormBase,
        eventErrorText: duplicateFormBase,
      },
      cannotScanUrl: {
        message: `We can't scan this URL. Try again, or submit to our
      <a href="https://customer.io/docs/api/track/#tag/Forms" target="_blank" rel="noopener noreferrer">forms endpoint</a>.`,
        eventErrorText: "We can't scan this URL.",
      },
      noFormsFound: {
        message: `No forms found under this address. <a href="https://customer.io/docs/connected-forms/#form-scanning-errors"
      target="_blank" rel="noopener noreferrer">Why not?</a>`,
        eventErrorText: 'No forms found under this address.',
      },
    };
  }

  get canSubmit() {
    if (this.args.isRescanning === true) {
      return this.auth.hasPermission('journeys/forms/edit');
    }

    return this.auth.hasPermission('journeys/forms/create');
  }

  @action
  updateFormName(name: string) {
    this.args.form.set('name', name);
  }

  @action
  onUrlToScanForFormsInput(urlToScanForForms: string) {
    if (this.alert) {
      this.alert = null;
    }
    this.formValues = {
      urlToScanForForms,
    };
    this.validations.validateAttribute('urlToScanForForms');
  }

  get urlToScanForFormsErrors() {
    if (!isEmpty(this.validations.errors.urlToScanForForms) && !isEmpty(this.formValues.urlToScanForForms)) {
      return this.validations.errors.urlToScanForForms;
    }
    return;
  }

  get navTabs() {
    let { form, isRescanning = false, campaignSidebar = false } = this.args;

    // if re-connecting a form, there'd only one nav tab: "Map Form Fields"
    // there's no sense in showing it, so let's hide the nav bar instead:
    if (!form.get('isNew') && !isRescanning) {
      return [];
    }

    let scannedFormsLength = this.scannedForms?.length ?? 0;

    let navTabs: { name: string; valid: boolean; label: string; disabled: boolean }[] = [
      {
        name: Tabs.FindForm,
        valid: Boolean(scannedFormsLength),
        label: campaignSidebar ? 'Find' : 'Find Form',
        disabled: false,
      },
      {
        name: Tabs.SelectForm,
        valid: this.currentTab !== Tabs.FindForm && Boolean(this.selectedScannedForm),
        label: campaignSidebar ? 'Select' : 'Select Form',
        disabled: scannedFormsLength === 0,
      },
      {
        name: Tabs.MapFormFields,
        valid:
          this.currentTab !== Tabs.FindForm &&
          this.currentTab !== Tabs.SelectForm &&
          !form.hasDuplicateFieldMapping &&
          form.identifierAttrMaps.length === 1 &&
          !this.formWithSameFieldsExists,
        label: campaignSidebar ? 'Map' : 'Map Form Fields',
        disabled: !this.selectedScannedForm || this.formWithSameFieldsExists || isEmpty(this.args.form.name),
      },
    ];

    return navTabs;
  }

  get fieldMappingErrorMessage() {
    if (!this.args.form?.identifierAttrMaps.length) {
      if (this.isIdentifiedByIdOnly) {
        return `You must map a field to id. If you want to identify people who submit your form by email instead, you
        can <a rel="noopener noreferrer" target="_blank" href="https://customer.io/docs/workspaces/#migrate-workspace"
        >set email as an identifier</a>`;
      }
      return `Must map to an identifier attribute (${joinWords(
        this.environment.current.enabledIdentifiers,
        ', ',
        ' or ',
      )})`;
    }
    if (this.args.form?.identifierAttrMaps.length > 1) {
      return `You can only map to one identifier, either ${joinWords(
        this.environment.current.enabledIdentifiers,
        ', ',
        ' or ',
      )}`;
    }
    if (this.args.form?.hasDuplicateFieldMapping) {
      return "Can't map more than one field to the same profile attribute.";
    }
    return null;
  }

  @not('environment.current.hasMultipleIdentifiers') isIdentifiedByIdOnly!: boolean;

  get mappingInstruction() {
    return `You must map a field to ${joinWords(
      this.environment.current.enabledIdentifiers,
      ', ',
      ' or ',
    )}. The rest are optional.`;
  }

  @computed('args.form', 'selectedScannedForm')
  get formWithSameFieldsExists() {
    if (this.duplicateForm != null) {
      this.analytics.track('integration', 'error_encountered', {
        name: 'Custom Forms',
        step: 'Select Form Tab',
        error: this.errorMessages.duplicateForm.eventErrorText,
        source: this.args.source,
      });
    }
    return this.duplicateForm != null;
  }

  get submitError() {
    if (this.currentTab === Tabs.FindForm && !this.formValues.urlToScanForForms) {
      return 'Enter URL to scan for forms.';
    } else if (this.currentTab === Tabs.SelectForm) {
      if (!this.selectedScannedForm) {
        return 'Select a form.';
      } else if (!this.args.form.name) {
        return 'Must enter form name.';
      } else if (this.formWithSameFieldsExists) {
        return this.errorMessages.duplicateForm.message;
      }
    } else if (this.currentTab === Tabs.MapFormFields) {
      return this.fieldMappingErrorMessage;
    }

    return null;
  }

  submitTask = task(async (event: Event) => {
    event.preventDefault();

    let waiterToken = FORM_WAITER.beginAsync();
    // connecting a form: navigate between tabs, save, or close the modal - as needed

    let scannedForms: ScannedForm[] = [];
    if (this.currentTab === Tabs.FindForm) {
      let { urlToScanForForms: url } = this.formValues;
      if (!url.startsWith('http')) {
        url = `https://${url}`;
      }
      try {
        scannedForms = await this.customForms.scanURLForForms(url);
      } catch (e) {
        this.alert = {
          type: 'error',
        };
        this.analytics.track('integration', 'error_encountered', {
          name: 'Custom Forms',
          step: 'Find Form Tab',
          error: this.errorMessages.cannotScanUrl.eventErrorText,
          source: this.args.source,
        });

        FORM_WAITER.endAsync(waiterToken);
        return;
      }

      this.scannedForms = scannedForms;
      // pre-select scanned form if there is only one, un-set it otherwise for selection on next setup step:
      if (this.scannedForms?.length === 1) {
        this.selectScannedForm(this.scannedForms[0]);
      } else {
        this.selectScannedForm(null);
      }

      if (this.scannedForms?.length) {
        this.goToTab(Tabs.SelectForm);
      }

      if (!this.scannedForms?.length) {
        this.analytics.track('integration', 'error_encountered', {
          name: 'Custom Forms',
          step: 'Find Form Tab',
          error: this.errorMessages.noFormsFound.eventErrorText,
          source: this.args.source,
        });
        this.alert = {
          type: 'warning',
        };
      }
    } else if (this.currentTab === Tabs.SelectForm) {
      this.goToTab(Tabs.MapFormFields);
    } else if (this.currentTab === Tabs.MapFormFields) {
      if (this.args.form.get('isNew')) {
        await this.customForms.saveNewForm(this.args.form);
      } else if (this.args.isRescanning === true) {
        // skip success alert when re-scanning: a banner will be shown in the form modal instead
        await this.customForms.updateFormSettings(this.args.form, true);
      } else {
        try {
          this.args.form.set('status', CustomFormStatus.Connected);
          await this.customForms.connectForm(this.args.form);
        } catch (e) {
          this.args.form.set('status', CustomFormStatus.Disconnected);
          throw e;
        }
      }

      this.args.onConnectForm?.(this.args.form);
      this.connectedForm = true;
      this.args.onConnectedFormChange?.(this.connectedForm);

      this.goToTab(Tabs.Success);
    } else if (this.currentTab === Tabs.Success) {
      this.onClose();
    }
    FORM_WAITER.endAsync(waiterToken);
  });

  @action
  onClose() {
    // invoked from modal, which handles "onClose" reset, if not in Modal then need to make the "reset" in this component
    if (this.args.isInModal !== true) {
      if (this.connectedForm) {
        this.args.onConnectFormAndClose?.(this.args.form);
      }
      if (!this.args.form.get('isNew')) {
        this.args.form.rollbackAttributes();
      }
    }
    // finally call the "onClose"
    this.args.onClose();
  }

  @action selectScannedForm(sf: ScannedForm | null) {
    this.selectedScannedForm = sf;

    // unset form properties if no scanned form is selected after "Find Form" step submit:
    if (!sf) {
      this.args.form.setProperties({
        name: '',
        htmlFormId: '',
        htmlFormName: '',
        htmlFormUrl: '',
        attrMaps: [],
      });

      return;
    }

    // start with existing mappings if available: relevant to form re-scanning
    let previousAttrMaps = this.args.form.attrMaps;

    this.args.form.setProperties({
      htmlFormId: sf.id,
      htmlFormName: sf.name,
      htmlFormUrl: this.formValues.urlToScanForForms.trim(),
      htmlFormInputs: sf.inputs.filter((inp) => inp.type !== 'hidden').map((i) => i.name),
      attrMaps: [],
    });

    // use existing form name when re-scanning an existing form:
    if (this.args.isRescanning !== true) {
      this.args.form.set('name', sf.id !== '' || sf.name !== '' ? `${sf.id || sf.name}` : '');
    }

    sf.inputs.forEach(
      (inp) =>
        (this.args.form.attrMaps = [
          ...this.args.form.attrMaps,
          {
            formFieldKey: inp.name,
            formFieldType: inp.type,
            formFieldLabel: '',
            cioAttr:
              previousAttrMaps.find((c) => c.formFieldKey === inp.name)?.cioAttr ?? underscore(inp.name.toLowerCase()),
          },
        ]),
    );

    // attempt to auto-map first <input type="email"> to "email" ID attribute:
    if (this.environment.current.canIdentifyByEmail) {
      let idAttrAlreadyMapped = false; // don't do anything if any ID attribute is already mapped

      let firstEmailInputMapping: CustomFormAttrMap | null = null;
      let firstEmailInputMappingIndex = -1;
      for (let [i, m] of this.args.form.attrMaps.entries()) {
        if ((this.environment.current.enabledIdentifiers as string[]).includes(m.cioAttr)) {
          idAttrAlreadyMapped = true;
          break;
        }

        if (firstEmailInputMapping == null && m.formFieldType === 'email') {
          firstEmailInputMapping = m;
          firstEmailInputMappingIndex = i;
        }
      }

      if (!idAttrAlreadyMapped && firstEmailInputMapping != null && firstEmailInputMappingIndex !== -1) {
        this.args.form.updateAttrMapAtIndex(firstEmailInputMappingIndex, {
          ...firstEmailInputMapping,
          cioAttr: 'email',
        });
      }
    }
  }

  @action
  goToTab(tab: Tabs) {
    if (this.currentTab === tab) {
      return;
    }

    if (tab === Tabs.FindForm) {
      this.analytics.track('forms', 'setup_find_form_tab_viewed', {
        from: this.args.source,
        rescanning: this.args.isRescanning,
      });
    } else if (tab === Tabs.SelectForm) {
      this.analytics.track('forms', 'setup_select_form_tab_viewed', {
        from: this.args.source,
        formsCount: this.scannedForms?.length,
        formFieldsCounts: this.scannedForms?.map((sf) => sf.inputs.length),
      });
    } else if (tab === Tabs.MapFormFields) {
      this.analytics.track('forms', 'setup_map_form_fields_tab_viewed', {
        from: this.args.source,
      });
    }

    this.currentTab = tab;
    this.args.onTabChange?.(tab);
  }

  @action
  closeBeforeTransition() {
    // Running the `onClose` callback on click directly in the template leads
    // to a reload of the page. Instead, let's wait a tick then close the modal.
    next(() => {
      this.onClose();
    });
  }

  @action
  selectDuplicateForm() {
    if (this.duplicateForm != null) {
      this.connectedForm = true;
      this.args.onConnectFormAndClose?.(this.duplicateForm);
    }
  }
}
